import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Chip,
  TableRow,
  TableCell,
  IconButton,
  PeopleIcon,
  ContentCopyIcon,
  CheckIcon,
  Text,
} from '@citibox-panel/ui';
import type { Delivery } from '@citibox-panel/api-client';
import { deliveryStateChipLabel, deliveryStateChipColor } from '../../../lib/deliveryStates';
import { formatDeliveryDateTime } from '../../../lib/formatDeliveryDateTime';
import { displayedPickupDate } from '../lib/displayedPickupDate';
import { buildDeliveryClipboardText } from '../lib/buildDeliveryClipboardText';
import { buildDeliveryClipboardHtml } from '../lib/buildDeliveryClipboardHtml';
import type { ClipboardRecipient } from '../lib/deliveryClipboardCells';

// How wide the name column may grow. A `<td>` sizes to its content, so without an explicit cap
// the column widens instead of truncating and the ellipsis never appears.
const NAME_MAX_WIDTH = 200;

export interface DeliveryRowProps {
  delivery: Delivery;
  selected: boolean;
  onOpen: () => void;
  // The open «Usuario»'s account phone, appended to the copied payload (not on the row).
  phone: string;
  // Signalled after a successful clipboard write, so the parent can report the copy.
  onCopied: () => void;
  // This delivery's titular, for a list that shows one. Its ABSENCE is what keeps the «Usuario»
  // card's table at eight cells: that list does not pass it and the two cells below never render.
  // Present with fields at `null` is the other state — the column exists and the value does not,
  // which is what paints «—». Same type the clipboard takes, so the row copies what it shows.
  recipient?: ClipboardRecipient;
}

// How long the success check replaces the copy glyph after a confirmed write.
const COPIED_FEEDBACK_MS = 1500;

// Write the row to the clipboard with BOTH flavours: text/html (a real <table>) so rich-text
// targets — Notion, Google Docs — rebuild an actual table, and text/plain (the TSV) as the
// fallback for spreadsheets/terminals. writeText() alone writes only text/plain, which Notion
// pastes as a text block rather than a table (Zendesk parses the TSV; Notion needs the HTML).
// Falls back to writeText on browsers without ClipboardItem/clipboard.write.
// The titular reaches BOTH flavours or neither: they share one ClipboardItem, so a payload that
// carried it in the TSV and not the HTML would paste a table whose head and body disagree.
function copyDeliveryToClipboard(
  delivery: Delivery,
  phone: string,
  recipient?: ClipboardRecipient,
): Promise<void> {
  const text = buildDeliveryClipboardText(delivery, phone, recipient);
  if (typeof ClipboardItem === 'function' && typeof navigator.clipboard.write === 'function') {
    const item = new ClipboardItem({
      'text/html': new Blob([buildDeliveryClipboardHtml(delivery, phone, recipient)], {
        type: 'text/html',
      }),
      'text/plain': new Blob([text], { type: 'text/plain' }),
    });
    return navigator.clipboard.write([item]);
  }
  return navigator.clipboard.writeText(text);
}

// One row of the «Entregas» card. The leading cell flags a delivery shared with this
// «Usuario» (owned by someone else) with a group icon — no names, those live in the
// delivery detail. Dates render in the locker's timezone with the time of day; the status
// chip shows the CC-friendly ES label, failing open to the capitalized raw string for
// unknown states; absent optional fields show an em dash. The trailing cell copies the row
// (plus the account phone) to the clipboard as both an HTML table and tab-separated text, so
// it pastes as a real table into rich-text targets (Notion, Docs) and Zendesk alike.
export function DeliveryRow({
  delivery,
  selected,
  onOpen,
  phone,
  onCopied,
  recipient,
}: DeliveryRowProps) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Clear a pending revert on unmount so it never fires on a gone component.
  useEffect(
    () => () => {
      clearTimeout(timer.current);
    },
    [],
  );

  const handleCopy = (): void => {
    // Success-gated: the check shows only once the write resolves; a failure degrades
    // silently (no check, no error UI). A re-click re-copies and resets the revert timer.
    copyDeliveryToClipboard(delivery, phone, recipient)
      .then(() => {
        setCopied(true);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
          setCopied(false);
        }, COPIED_FEEDBACK_MS);
        onCopied();
      })
      .catch(() => {
        // The missing check is the only failure signal in this version.
      });
  };

  return (
    <TableRow selected={selected} onClick={onOpen}>
      <TableCell>
        {delivery.sharedWithRecipient ? (
          // TODO: i18n
          <Box sx={{ display: 'flex' }}>
            <PeopleIcon fontSize="small" aria-label="Compartida con este usuario" />
          </Box>
        ) : null}
      </TableCell>
      <TableCell>{formatDeliveryDateTime(delivery.storedDate, delivery.lockerTimezone)}</TableCell>
      <TableCell>{formatDeliveryDateTime(displayedPickupDate(delivery), delivery.lockerTimezone)}</TableCell>
      <TableCell>
        <Chip label={deliveryStateChipLabel(delivery.status)} color={deliveryStateChipColor(delivery.status)} />
      </TableCell>
      <TableCell>{delivery.boxNumber ?? '—'}</TableCell>
      <TableCell>{delivery.tracking ?? '—'}</TableCell>
      <TableCell>{delivery.carrier ?? '—'}</TableCell>
      {recipient === undefined ? null : (
        <>
          {/* Never truncated: half a phone number is worse than one that overflows its column. */}
          <TableCell singleLine>{recipient.phone ?? '—'}</TableCell>
          <TableCell>
            {/* `title` by conditional spread, not `title={name ?? undefined}`: the project runs
                with exactOptionalPropertyTypes and the façade prop is `title?: string`, so an
                explicit undefined does not compile. It also keeps an em dash from carrying an
                empty tooltip. */}
            <Box sx={{ maxWidth: NAME_MAX_WIDTH }}>
              <Text
                variant="body2"
                noWrap
                {...(recipient.name !== null ? { title: recipient.name } : {})}
              >
                {recipient.name ?? '—'}
              </Text>
            </Box>
          </TableCell>
        </>
      )}
      <TableCell align="right">
        {/* TODO: i18n */}
        <IconButton
          size="small"
          stopPropagation
          aria-label="Copiar datos de la entrega"
          onClick={handleCopy}
        >
          {copied ? <CheckIcon color="success" /> : <ContentCopyIcon />}
        </IconButton>
      </TableCell>
    </TableRow>
  );
}
